import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getAuth, onIdTokenChanged, User } from 'firebase/auth';
import { db } from '../services/firebase';
import type { PlanId, EntitlementKey } from '../config/entitlements';
import { getEntitlementsForPlanId, resolvePlanId } from '../config/entitlements';

export type SubscriptionTier = 'free' | 'basic' | 'pro';
export type SubscriptionStatus = 'active' | 'cancelled' | 'expired' | 'error';
const API_BASE = process.env.REACT_APP_API_URL || '';

interface SubscriptionData {
  plan?: string;
  status: SubscriptionStatus;
  signUpDate: any; // Firestore Timestamp
  expiryDate: any; // Firestore Timestamp | null
  lastUpdated: any; // Firestore Timestamp
  plan_id?: string;
  billing_term?: string;
}

interface BackendEntitlementData {
  active?: boolean;
  plan_id?: string | null;
  current_period_end?: string | null;
  billing_term?: string | null;
  entitlements?: string[];
  verification_failed?: boolean;
}

interface SubscriptionState {
  data: SubscriptionData | null;
  effectiveTier: SubscriptionTier;
  planId: PlanId | null;
  entitlements: readonly EntitlementKey[];
  loading: boolean;
  error: string | null;
  isSubscriptionActive: boolean;
  isPro: boolean;
  isBasic: boolean;
  isFree: boolean;
  canAccess: (tier: SubscriptionTier) => boolean;
  refreshToken: () => Promise<void>;
}

export function deriveSubscriptionStateOnError(
  previous: Pick<SubscriptionState, 'data' | 'planId' | 'refreshToken'>
): Pick<
  SubscriptionState,
  | 'data'
  | 'effectiveTier'
  | 'planId'
  | 'entitlements'
  | 'loading'
  | 'error'
  | 'isSubscriptionActive'
  | 'isPro'
  | 'isBasic'
  | 'isFree'
  | 'refreshToken'
> {
  return {
    data: previous.data ?? null,
    effectiveTier: 'free',
    planId: previous.planId ?? null,
    entitlements: [],
    loading: false,
    error: 'Failed to load subscription data',
    isSubscriptionActive: false,
    isPro: false,
    isBasic: false,
    isFree: true,
    refreshToken: previous.refreshToken,
  };
}

function isSubscriptionCurrentlyActive(data: SubscriptionData | null): boolean {
  if (!data) return false;
  return data.status === 'active' && (!data.expiryDate || data.expiryDate.toMillis() > Date.now());
}

function getEffectiveTier(planId: PlanId | null, hasActiveAccess: boolean): SubscriptionTier {
  if (!hasActiveAccess) return 'free';
  return planId === 'all_access' ? 'pro' : 'basic';
}

function mergeEntitlements(...lists: Array<ReadonlyArray<EntitlementKey | string | null | undefined>>) {
  const flattened = lists
    .flatMap((list) => list || [])
    .filter((value): value is EntitlementKey => typeof value === 'string' && value.length > 0);
  return Array.from(new Set(flattened));
}

async function fetchLiveEntitlement(user: User): Promise<BackendEntitlementData | null> {
  const token = await user.getIdToken();
  const response = await fetch(`${API_BASE}/api/account/entitlement`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to load entitlement (${response.status})`);
  }
  return response.json();
}

async function buildSubscriptionState(
  user: User,
  tokenPlanId: string | undefined,
  firestoreData: SubscriptionData | null
): Promise<Omit<SubscriptionState, 'refreshToken'>> {
  const liveEntitlement = await fetchLiveEntitlement(user).catch(() => null);
  const isFirestoreActive = isSubscriptionCurrentlyActive(firestoreData);
  const liveEntitlements = Array.isArray(liveEntitlement?.entitlements)
    ? liveEntitlement!.entitlements.filter((ent): ent is EntitlementKey => typeof ent === 'string')
    : [];
  const planId: PlanId | null = resolvePlanId(
    typeof tokenPlanId === 'string' ? tokenPlanId : liveEntitlement?.plan_id ?? firestoreData?.plan_id,
    firestoreData?.plan
  );
  const firestoreEntitlements = isFirestoreActive && planId != null ? getEntitlementsForPlanId(planId) : [];
  const entitlements = mergeEntitlements(firestoreEntitlements, liveEntitlements);
  const hasActiveAccess = entitlements.length > 0 || (Boolean(liveEntitlement?.active) && liveEntitlements.length > 0);
  const effectiveTier = getEffectiveTier(planId, hasActiveAccess);

  return {
    data: firestoreData,
    effectiveTier,
    planId,
    entitlements,
    loading: false,
    error: liveEntitlement?.verification_failed ? 'Failed to load subscription data' : null,
    isSubscriptionActive: hasActiveAccess,
    isPro: hasActiveAccess && planId === 'all_access',
    isBasic: hasActiveAccess && planId !== 'all_access',
    isFree: !hasActiveAccess,
    canAccess: (tier: SubscriptionTier) => {
      const tierOrder = { free: 0, basic: 1, pro: 2 };
      return tierOrder[effectiveTier] >= tierOrder[tier];
    },
  };
}

export function useSubscription(): SubscriptionState {
  const [state, setState] = useState<SubscriptionState>({
    data: null,
    effectiveTier: 'free',
    planId: null,
    entitlements: [],
    loading: true,
    error: null,
    isSubscriptionActive: false,
    isPro: false,
    isBasic: false,
    isFree: true,
    canAccess: () => false,
    refreshToken: async () => {},
  });

  useEffect(() => {
    const auth = getAuth();
    let unsubscribeFirestore: () => void;

    const unsubscribeAuth = onIdTokenChanged(auth, async (user: User | null) => {
      if (!user) {
        setState(prev => ({
          ...prev,
          loading: false,
          effectiveTier: 'free',
          planId: null,
          entitlements: [],
          isSubscriptionActive: false,
          isPro: false,
          isBasic: false,
          isFree: true,
          canAccess: (tier: SubscriptionTier) => tier === 'free'
        }));
        return;
      }

      const token = await user.getIdTokenResult();

      const subRef = doc(db, 'users', user.uid, 'subscriptions', 'current');
      unsubscribeFirestore = onSnapshot(subRef, 
        async (docSnap) => {
          const data = docSnap.exists() ? (docSnap.data() as SubscriptionData) : null;
          try {
            const nextState = await buildSubscriptionState(
              user,
              typeof token.claims.plan_id === 'string' ? token.claims.plan_id : undefined,
              data
            );
            setState({
              ...nextState,
              refreshToken: async () => {
                await user.getIdToken(true);
                const newToken = await user.getIdTokenResult();
                const refreshedState = await buildSubscriptionState(
                  user,
                  typeof newToken.claims.plan_id === 'string' ? newToken.claims.plan_id : undefined,
                  data
                );
                setState(prev => ({
                  ...prev,
                  ...refreshedState,
                }));
              }
            });
          } catch (stateError) {
            setState(prev => ({
              ...prev,
              ...deriveSubscriptionStateOnError(prev),
            }));
          }
        },
        (error) => {
          setState(prev => ({
            ...prev,
            ...deriveSubscriptionStateOnError(prev),
          }));
        }
      );
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeFirestore) unsubscribeFirestore();
    };
  }, []);

  return state;
} 
